import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, Search, Filter, Download, Calendar, User, Activity,
  ChevronDown, ChevronUp, RefreshCw, Trash2, Clock, Database, Eye,
  XCircle, CheckCircle, AlertTriangle, Info
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import api from '../../services/api';

const AuditLogsTab = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedLog, setExpandedLog] = useState(null);
  const [filters, setFilters] = useState({
    category: '',
    user_id: '',
    start_date: '',
    end_date: '',
    timeframe: '30' // Quick timeframe filter
  });
  const [showFilters, setShowFilters] = useState(false);

  const categories = [
    { value: 'booking', label: 'Bookings', icon: '📅' },
    { value: 'payment', label: 'Payments', icon: '💳' },
    { value: 'user', label: 'Users', icon: '👤' },
    { value: 'worker', label: 'Workers', icon: '👷' },
    { value: 'service', label: 'Services', icon: '🛠️' },
    { value: 'payroll', label: 'Payroll', icon: '💰' },
    { value: 'time_clock', label: 'Time Clock', icon: '⏰' },
    { value: 'coupon', label: 'Coupons', icon: '🎟️' },
    { value: 'chat', label: 'Chat', icon: '💬' },
    { value: 'admin', label: 'Admin', icon: '⚙️' },
    { value: 'auth', label: 'Authentication', icon: '🔐' },
    { value: 'system', label: 'System', icon: '🖥️' },
    { value: 'dispute', label: 'Disputes', icon: '⚖️' },
    { value: 'settings', label: 'Settings', icon: '🔧' },
    { value: 'shift', label: 'Shifts', icon: '📆' }
  ];

  const timeframes = [
    { value: '1', label: 'Today' },
    { value: '7', label: 'Last 7 Days' },
    { value: '30', label: 'Last 30 Days' },
    { value: '90', label: 'Last 90 Days' },
    { value: 'custom', label: 'Custom Range' }
  ];

  useEffect(() => {
    fetchData();
    fetchUsers();
     
  }, [filters.category, filters.user_id, filters.timeframe]);

  useEffect(() => {
    if (filters.start_date && filters.end_date) {
      fetchData();
    }
     
  }, [filters.start_date, filters.end_date]);

  const fetchUsers = async () => {
    try {
      const res = await api.get('/users?limit=100');
      setUsers(res.data || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.category) params.append('category', filters.category);
      if (filters.user_id) params.append('user_id', filters.user_id);
      
      // Handle timeframe vs custom dates
      if (filters.timeframe !== 'custom') {
        const days = parseInt(filters.timeframe);
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - days);
        params.append('start_date', startDate.toISOString().split('T')[0]);
      } else {
        if (filters.start_date) params.append('start_date', filters.start_date);
        if (filters.end_date) params.append('end_date', filters.end_date);
      }
      
      const [logsRes, statsRes] = await Promise.all([
        api.get(`/audit?${params.toString()}&limit=200`),
        api.get(`/audit/stats?days=${filters.timeframe === 'custom' ? 30 : filters.timeframe}`)
      ]);
      setLogs(logsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (logs.length === 0) {
      toast.error('No logs to export');
      return;
    }
    
    const csvContent = [
      ['Timestamp', 'Category', 'Action', 'Description', 'User', 'Resource Type', 'Resource ID'].join(','),
      ...logs.map(log => [
        new Date(log.timestamp).toISOString(),
        log.category,
        log.action,
        `"${(log.description || '').replace(/"/g, '""')}"`,
        log.user_name,
        log.resource_type || '',
        log.resource_id || ''
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `audit_logs_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Audit logs exported');
  };

  const getActionIcon = (action) => {
    if (action?.toLowerCase().includes('delete') || action?.toLowerCase().includes('remove')) {
      return <XCircle className="w-4 h-4 text-[var(--error)]" />;
    }
    if (action?.toLowerCase().includes('create') || action?.toLowerCase().includes('add')) {
      return <CheckCircle className="w-4 h-4 text-[var(--success)]" />;
    }
    if (action?.toLowerCase().includes('update') || action?.toLowerCase().includes('edit')) {
      return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
    }
    return <Info className="w-4 h-4 text-[#D4AF37]" />;
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      fetchData();
      return;
    }
    setLoading(true);
    try {
      const res = await api.get(`/audit/search?q=${encodeURIComponent(searchQuery)}`);
      setLogs(res.data);
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (category) => {
    const cat = categories.find(c => c.value === category);
    return cat?.icon || '📋';
  };

  const getCategoryColor = (category) => {
    const colors = {
      booking: 'bg-[#D4AF37]/10 text-[#D4AF37]',
      payment: 'bg-[var(--success)]/15 text-green-700',
      user: 'bg-[#F4C2C2]/15 text-purple-700',
      worker: 'bg-orange-100 text-orange-700',
      service: 'bg-teal-100 text-teal-700',
      payroll: 'bg-[var(--warning)]/15 text-yellow-700',
      time_clock: 'bg-indigo-100 text-indigo-700',
      coupon: 'bg-pink-100 text-pink-700',
      chat: 'bg-cyan-100 text-cyan-700',
      admin: 'bg-[var(--error)]/15 text-red-700',
      auth: 'bg-amber-100 text-amber-700',
      system: 'bg-[var(--background-subtle)] text-[var(--text-primary)]',
      dispute: 'bg-rose-100 text-rose-700',
      settings: 'bg-slate-100 text-slate-700'
    };
    return colors[category] || 'bg-[var(--background-subtle)] text-[var(--text-primary)]';
  };

  const formatTimestamp = (ts) => {
    const date = new Date(ts);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (loading && logs.length === 0) {
    return (
      <div className="p-12 text-center">
        <div className="animate-spin w-8 h-8 border-2 border-[var(--primary)] border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-[var(--text-muted)]">Loading audit logs...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold font-['Cormorant_Garamond'] flex items-center gap-2">
            <Database className="w-6 h-6 text-[var(--primary)]" />
            Audit Logs
          </h2>
          <p className="text-[var(--text-muted)] text-sm mt-1">Complete activity trail for your business</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleExport} data-testid="export-logs">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
          <Button variant="outline" onClick={fetchData} data-testid="refresh-logs">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {/* Quick Timeframe Filter */}
      <div className="flex flex-wrap gap-2">
        {timeframes.map(tf => (
          <button
            key={tf.value}
            onClick={() => setFilters(prev => ({ ...prev, timeframe: tf.value }))}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              filters.timeframe === tf.value
                ? 'bg-[var(--primary)] text-white'
                : 'bg-[var(--background-subtle)] text-[var(--text-primary)] hover:bg-[var(--background-subtle)]'
            }`}
            data-testid={`timeframe-${tf.value}`}
          >
            {tf.label}
          </button>
        ))}
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card p-4">
            <p className="text-3xl font-bold text-[var(--primary)]">{stats.total_logs}</p>
            <p className="text-sm text-[var(--text-muted)]">Total Events (30 days)</p>
          </div>
          <div className="card p-4">
            <p className="text-3xl font-bold text-[#D4AF37]">{Object.keys(stats.by_category || {}).length}</p>
            <p className="text-sm text-[var(--text-muted)]">Active Categories</p>
          </div>
          <div className="card p-4">
            <p className="text-3xl font-bold text-[var(--success)]">{stats.top_users?.length || 0}</p>
            <p className="text-sm text-[var(--text-muted)]">Active Users</p>
          </div>
          <div className="card p-4">
            <p className="text-3xl font-bold text-[#F4C2C2]">{stats.top_actions?.length || 0}</p>
            <p className="text-sm text-[var(--text-muted)]">Action Types</p>
          </div>
        </div>
      )}

      {/* Category Breakdown */}
      {stats?.by_category && Object.keys(stats.by_category).length > 0 && (
        <div className="card p-4">
          <p className="text-sm font-medium mb-3">Activity by Category</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(stats.by_category).sort((a, b) => b[1] - a[1]).map(([cat, count]) => (
              <button
                key={cat}
                onClick={() => setFilters({ ...filters, category: filters.category === cat ? '' : cat })}
                className={`px-3 py-1.5 rounded-full text-sm flex items-center gap-1 transition-all ${
                  filters.category === cat 
                    ? 'ring-2 ring-[var(--primary)] ' + getCategoryColor(cat)
                    : getCategoryColor(cat) + ' opacity-70 hover:opacity-100'
                }`}
              >
                <span>{getCategoryIcon(cat)}</span>
                <span className="capitalize">{cat.replace('_', ' ')}</span>
                <span className="font-bold ml-1">{count}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Search & Filters */}
      <div className="card p-4 space-y-4">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search logs by description, action, or user..."
              className="pl-10"
              data-testid="search-logs"
            />
          </div>
          <Button onClick={handleSearch}>Search</Button>
          <Button 
            variant="outline" 
            onClick={() => setShowFilters(!showFilters)}
            data-testid="toggle-filters"
          >
            <Filter className="w-4 h-4 mr-2" /> Filters
            <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </Button>
        </div>

        {showFilters && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            className="grid md:grid-cols-5 gap-4 pt-4 border-t"
          >
            <div>
              <label className="text-sm font-medium mb-1 block">Category</label>
              <select
                value={filters.category}
                onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                className="input-styled w-full"
              >
                <option value="">All Categories</option>
                {categories.map(c => (
                  <option key={c.value} value={c.value}>{c.icon} {c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">User</label>
              <select
                value={filters.user_id}
                onChange={(e) => setFilters({ ...filters, user_id: e.target.value })}
                className="input-styled w-full"
              >
                <option value="">All Users</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.full_name || u.email}</option>
                ))}
              </select>
            </div>
            {filters.timeframe === 'custom' && (
              <>
                <div>
                  <label className="text-sm font-medium mb-1 block">Start Date</label>
                  <Input
                    type="date"
                    value={filters.start_date}
                    onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">End Date</label>
                  <Input
                    type="date"
                    value={filters.end_date}
                    onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                  />
                </div>
              </>
            )}
            <div className="flex items-end">
              <Button 
                variant="outline" 
                onClick={() => setFilters({ category: '', user_id: '', start_date: '', end_date: '', timeframe: '30' })}
                className="w-full"
              >
                Clear Filters
              </Button>
            </div>
          </motion.div>
        )}
      </div>

      {/* Logs List */}
      <div className="space-y-2">
        {logs.length === 0 ? (
          <div className="card p-12 text-center">
            <FileText className="w-16 h-16 text-[var(--text-muted)] mx-auto mb-4" />
            <h3 className="text-lg font-bold mb-2">No Logs Found</h3>
            <p className="text-[var(--text-muted)]">Try adjusting your filters or search query.</p>
          </div>
        ) : (
          logs.map((log) => (
            <motion.div
              key={log.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="card overflow-hidden hover:shadow-sm transition-shadow"
              data-testid={`log-${log.id}`}
            >
              <div 
                className="p-4 cursor-pointer"
                onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg ${getCategoryColor(log.category)}`}>
                    {getCategoryIcon(log.category)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {getActionIcon(log.action)}
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getCategoryColor(log.category)}`}>
                        {log.category}
                      </span>
                      <span className="text-sm font-medium">{log.action}</span>
                    </div>
                    <p className="text-sm text-[var(--text-secondary)] mt-1 line-clamp-1">{log.description || 'No description'}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-[var(--text-muted)]">
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {log.user_name}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatTimestamp(log.timestamp)}
                      </span>
                      {log.resource_id && (
                        <span className="font-mono text-[10px] bg-[var(--background-subtle)] px-1 rounded">
                          {log.resource_type}: {log.resource_id.slice(0, 8)}...
                        </span>
                      )}
                    </div>
                  </div>
                  <Button variant="ghost" size="sm">
                    {expandedLog === log.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              
              {/* Expanded Details */}
              <AnimatePresence>
                {expandedLog === log.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="border-t bg-[var(--background-subtle)] p-4"
                  >
                    <div className="grid md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-[var(--text-muted)] text-xs uppercase font-medium mb-1">Full Description</p>
                        <p>{log.description || 'No description provided'}</p>
                      </div>
                      <div>
                        <p className="text-[var(--text-muted)] text-xs uppercase font-medium mb-1">Timestamp</p>
                        <p>{new Date(log.timestamp).toLocaleString()}</p>
                      </div>
                      {log.resource_type && (
                        <div>
                          <p className="text-[var(--text-muted)] text-xs uppercase font-medium mb-1">Resource</p>
                          <p className="font-mono text-xs">{log.resource_type}: {log.resource_id}</p>
                        </div>
                      )}
                      {log.ip_address && (
                        <div>
                          <p className="text-[var(--text-muted)] text-xs uppercase font-medium mb-1">IP Address</p>
                          <p className="font-mono text-xs">{log.ip_address}</p>
                        </div>
                      )}
                      {log.metadata && Object.keys(log.metadata).length > 0 && (
                        <div className="md:col-span-2">
                          <p className="text-[var(--text-muted)] text-xs uppercase font-medium mb-1">Additional Data</p>
                          <pre className="text-xs bg-[var(--background-subtle)] p-2 rounded overflow-auto max-h-32">
                            {JSON.stringify(log.metadata, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))
        )}
      </div>

      {/* Load More */}
      {logs.length >= 100 && (
        <div className="text-center">
          <p className="text-sm text-[var(--text-muted)]">Showing first 100 logs. Use filters to narrow results.</p>
        </div>
      )}
    </div>
  );
};

export default AuditLogsTab;
